import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../.shared/header/header';
import Footer from '../../.shared/footer/footer';
import PatientStories from '../../.shared/PatientStories/PatientStories';
import FAQ from '../../.shared/FAQ/FAQ';
import AsSeenOn from '../../.shared/AsSeenOn/AsSeenOn';
import HowItWorks from '../../.shared/HowItWorks/HowItWorks';
import CyclingText from '../../Landing/CyclingText';
import Pricing from '../../../data/Pricing';
import { trackPostHogEvent } from '../../../utils/posthogUtils';
import { startInterview } from '../../../utils/interviewFlow';
import styles from './SemaglutideLanding.module.css';
import heroPortrait from '../../../assets/semaglutide-hero-portrait.webp';
import vialImage from '../../../assets/semaglutide-vial.webp';

const SAFETY_MESSAGES = [
    'Compounded medication — not FDA-approved',
    'A licensed clinician evaluates every intake',
    'Prescription included if clinically appropriate',
    'Dispensed by a state-licensed pharmacy',
    'Not evaluated as equivalent to brand-name versions',
    'Individual results may vary',
];

const TABS = [
    {
        id: 'included',
        label: "What's included",
        body: 'Clinician evaluation, ongoing care, and — if prescribed — medication, supplies, and shipping. One subscription, no surprise add-ons. Price includes all doses.',
    },
    {
        id: 'shipping',
        label: 'How it ships',
        body: 'If prescribed, your medication is dispensed by a state-licensed pharmacy. Many compounded GLP-1s require refrigeration at 36–46°F (2–8°C); detailed handling instructions are included.',
    },
    {
        id: 'providers',
        label: 'Provider roles',
        body: 'Platform facilitates care. Clinicians evaluate and prescribe independently. Pharmacies dispense.',
    },
];

const LOCKUP_SENTENCES = [
    'Once-weekly compounded semaglutide, prescribed only when clinically appropriate.',
    'A licensed clinician reviews every intake and guides dose adjustments over time.',
    'Semaglutide is a GLP-1 receptor agonist — a class of prescription medication.',
];

const SIDE_EFFECTS = ['Nausea', 'Vomiting', 'Diarrhea', 'Abdominal discomfort', 'Decreased appetite'];

export default function SemaglutideLanding() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(TABS[0].id);

    const pricing = Pricing();
    const semaglutide = pricing.getPricingOptionsByProductName('semaglutide');
    const monthlyPrice = semaglutide?.injectable?.monthly?.total?.split('.')[0] ?? null;
    const quarterlyPrice = semaglutide?.injectable?.threeMonths?.total?.split('.')[0] ?? null;

    const handleGetStarted = () => {
        // Enroll before tracking, so trackExposure has registered the variant
        // on this person and the submit event below carries it.
        const flow = startInterview();

        trackPostHogEvent('landing_page_submitted', {
            selected: 'hero_get_started',
            to_path: '/interview',
            interview_flow: flow.id
        });
        navigate(flow.entryPath);
    };

    return (
        <div className={styles.page}>
            <div className={styles.safetyBar}>
                <span className={styles.safetyDot} aria-hidden="true">•</span>
                <CyclingText phrases={SAFETY_MESSAGES} className={styles.safetyText} interval={3500} />
            </div>

            <Header hideNavigation />

            <section className={styles.hero}>
                <div className={styles.heroImage}>
                    <img src={heroPortrait} alt="Patient holding a tb2.health compounded semaglutide vial" />
                </div>
                <div className={styles.heroText}>
                    <div className={styles.heroEyebrow}>
                        <span className={styles.heroEyebrowA}>Compounded</span>
                        <span className={styles.heroEyebrowB}>GLP-1 Care</span>
                    </div>
                    <h1 className={styles.heroTitle}>
                        <span className={styles.grad}>Clinician-Guided GLP-1 Care</span>
                    </h1>
                    <p className={styles.heroSub}>Compounded Semaglutide · Weight management support through personalized care</p>
                    <p className={styles.heroSubSmall}>Complete your intake from your phone in minutes. See pricing and subscription terms before you begin.</p>
                    <button type="button" className={styles.heroCta} onClick={handleGetStarted}>Get Started</button>

                    {monthlyPrice && (
                        <div className={styles.priceLine}>
                            ${monthlyPrice}
                            <span className={styles.priceMonth}>/month{quarterlyPrice ? ` or $${quarterlyPrice} for 3 months` : ''}</span>
                            <span className={styles.priceCancel}>Cancel anytime</span>
                        </div>
                    )}
                </div>

                {/* Sibling of .heroText (not nested) so mobile can order the image
                    between the CTA block and the tabs. */}
                <div className={styles.tabs}>
                    <div className={styles.tabRow} role="tablist">
                        {TABS.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                aria-selected={activeTab === tab.id}
                                className={`${styles.tabBtn} ${activeTab === tab.id ? styles.tabBtnActive : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles.tabPanels}>
                        {TABS.map((tab) => (
                            activeTab === tab.id && <div key={tab.id} className={styles.tabPanel}>{tab.body}</div>
                        ))}
                    </div>
                </div>
            </section>

            <AsSeenOn />

            <section className={styles.lockup}>
                <div className={styles.lockupInner}>
                    <div className={styles.lockupVial}>
                        <img src={vialImage} alt="tb2.health compounded semaglutide injection vial" />
                    </div>
                    <div className={styles.lockupText}>
                        <div className={styles.lockupEyebrow}>Compounded GLP-1 Care</div>
                        <h2 className={styles.lockupName}>Compounded <span className={styles.lockupAccent}>Semaglutide</span></h2>
                        <p className={styles.lockupCopy}>
                            {LOCKUP_SENTENCES.map((sentence) => (
                                <span key={sentence} className={styles.lockupSentence}>{sentence}</span>
                            ))}
                        </p>
                    </div>
                </div>
            </section>

            <HowItWorks />

            <PatientStories />

            <section className={styles.safety}>
                <div className={styles.safetyGrid}>
                    <div className={styles.safetyCol}>
                        <h3>Dosing &amp; expectations</h3>
                        <div className={styles.safetySub}>Clinician-directed</div>
                        <p>Your clinician determines dosing and adjusts over time. If prescribed, dose adjustments are included within your program.</p>
                        <p className={styles.safetyStrong}>Response varies. No guaranteed outcomes.</p>
                    </div>
                    <div className={styles.safetyCol}>
                        <h3>Possible side effects</h3>
                        <div className={styles.safetySub}>Disclose all medications</div>
                        <ul className={styles.safetyList}>
                            {SIDE_EFFECTS.map((effect) => (
                                <li key={effect}><span className={styles.safetyListDot}></span>{effect}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <FAQ />

            <Footer />
        </div>
    );
}
